// Shared pieces of the issue pages: the status and severity badges (always a word, never colour alone), the chips
// that link a conversation's evidence to its issues, and the two actions every piece of evidence offers: open a new
// issue from it, or link it to an open one.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { errorMessage } from '../../hooks/useAsync';
import { Alert, Badge, Button, Input, Select, type Tone } from '../../components/ui';
import type { IssueDetail, IssueLinkIn, IssueRef, IssueSeverity, IssueStatus } from '../../types/api';

export const ISSUE_STATUSES: IssueStatus[] = ['open', 'investigating', 'fixed', 'wont_fix', 'duplicate'];
export const ISSUE_SEVERITIES: IssueSeverity[] = ['low', 'medium', 'high', 'critical'];
const CLOSED: IssueStatus[] = ['fixed', 'wont_fix', 'duplicate'];

export function isClosed(status: IssueStatus): boolean {
  return CLOSED.includes(status);
}

function statusTone(status: IssueStatus): Tone {
  return status === 'open' ? 'bad' : status === 'investigating' ? 'warn' : status === 'fixed' ? 'good' : 'neutral';
}

function severityTone(severity: IssueSeverity): Tone {
  return severity === 'critical' || severity === 'high' ? 'bad' : severity === 'medium' ? 'warn' : 'neutral';
}

export function IssueStatusBadge({ status }: { status: IssueStatus }) {
  const { t } = useTranslation();
  return <Badge tone={statusTone(status)}>{t(`issues.status.${status}`, status)}</Badge>;
}

export function IssueSeverityBadge({ severity }: { severity: IssueSeverity }) {
  const { t } = useTranslation();
  return <Badge tone={severityTone(severity)}>{t(`issues.severity.${severity}`, severity)}</Badge>;
}

export function toRef(issue: IssueDetail): IssueRef {
  return { id: issue.id, title: issue.title, status: issue.status, severity: issue.severity };
}

/** The issues as links: `#id title` and the status, a closed one dimmed. */
export function IssueChips({ issues }: { issues: IssueRef[] }) {
  if (!issues.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="issue-chips">
      {issues.map((i) => (
        <Link key={i.id} to={`/issues/${i.id}`} title={i.title}
              className={`inline-flex max-w-xs items-center gap-1.5 rounded-full border border-line-card px-2 py-0.5 text-[12px] hover:bg-hover-soft ${isClosed(i.status) ? 'opacity-60' : ''}`}>
          <span className="font-mono text-[11px] text-faint">#{i.id}</span>
          <span className="truncate text-ink">{i.title}</span>
          <IssueStatusBadge status={i.status} />
        </Link>
      ))}
    </div>
  );
}

/** "New issue" and "Link to issue…" for one piece of evidence. The new issue starts from `title`, graded medium; the
 *  picker searches the open issues. Either answers the issue to the caller, who shows its chip. */
export function IssueActions({ link, title, onLinked }: { link: IssueLinkIn; title: string; onLinked: (issue: IssueRef) => void }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'new' | 'link' | null>(null);
  const [draft, setDraft] = useState(title);
  const [severity, setSeverity] = useState<IssueSeverity>('medium');
  const [q, setQ] = useState('');
  const [found, setFound] = useState<IssueRef[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const testid = `${link.kind}-${link.ref}`;

  useEffect(() => {
    if (mode !== 'link') return;
    let live = true;
    const timer = setTimeout(() => {
      api.issues({ status: 'open', q: q.trim() || undefined, page_size: 8 }).then(
        (page) => live && setFound(page.items),
        (err: unknown) => live && setError(errorMessage(err)),
      );
    }, 200);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [mode, q]);

  const run = async (call: () => Promise<IssueDetail>) => {
    setBusy(true);
    setError(null);
    try {
      onLinked(toRef(await call()));
      setMode(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap gap-1">
        <Button variant="ghost" className="h-7 px-2 text-[12px]" onClick={() => setMode(mode === 'new' ? null : 'new')} data-testid={`new-issue-${testid}`}>
          {t('issues.newFromThis')}
        </Button>
        <Button variant="ghost" className="h-7 px-2 text-[12px]" onClick={() => setMode(mode === 'link' ? null : 'link')} data-testid={`link-issue-${testid}`}>
          {t('issues.linkToIssue')}
        </Button>
      </div>
      {error && <Alert>{error}</Alert>}
      {mode === 'new' && (
        <div className="flex flex-col gap-1.5 rounded-md border border-line-soft p-2">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={t('issues.titlePlaceholder')} data-testid={`new-issue-title-${testid}`} />
          <div className="flex gap-1.5">
            <Select value={severity} onChange={(e) => setSeverity(e.target.value as IssueSeverity)} aria-label={t('issues.severityLabel')}>
              {ISSUE_SEVERITIES.map((s) => (
                <option key={s} value={s}>{t(`issues.severity.${s}`)}</option>
              ))}
            </Select>
            <Button variant="primary" busy={busy} disabled={!draft.trim()} data-testid={`create-issue-${testid}`}
                    onClick={() => void run(() => api.createIssue({ title: draft.trim(), severity, links: [link] }))}>
              {t('issues.create')}
            </Button>
          </div>
        </div>
      )}
      {mode === 'link' && (
        <div className="flex flex-col gap-1.5 rounded-md border border-line-soft p-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('issues.searchOpen')} autoFocus data-testid={`issue-search-${testid}`} />
          <ul className="flex max-h-56 flex-col overflow-auto">
            {found.map((i) => (
              <li key={i.id}>
                <button type="button" disabled={busy} onClick={() => void run(() => api.linkIssue(i.id, link))}
                        className="flex w-full items-center gap-2 rounded-sm px-1.5 py-1 text-left text-[12.5px] hover:bg-hover-soft" data-testid={`pick-issue-${i.id}`}>
                  <span className="font-mono text-[11px] text-faint">#{i.id}</span>
                  <span className="truncate text-ink">{i.title}</span>
                  <IssueSeverityBadge severity={i.severity} />
                </button>
              </li>
            ))}
            {!found.length && <li className="px-1.5 py-1 text-[12px] text-faint">{t('issues.noneFound')}</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
